import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useCustomer } from "@/lib/customer";
import { StampCard } from "@/components/StampCard";
import { RewardsHistory } from "@/components/RewardsHistory";
import { OrderHistory } from "@/components/OrderHistory";
import { CustomerAuthForm } from "@/components/CustomerAuthForm";
import { ChefHat, Loader2, ArrowLeft, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import { LanguagePicker } from "@/components/LanguagePicker";

export default function CustomerAccount() {
  const { t } = useT();
  const { restaurantId } = useParams();
  const [params] = useSearchParams();
  const table = params.get("table");
  const { card, setCard, logout } = useCustomer(restaurantId);
  const [restaurant, setRestaurant] = useState(null);
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    api.get(`/restaurants/${restaurantId}`).then((r) => setRestaurant(r.data)).catch(() => {});
  }, [restaurantId]);

  useEffect(() => {
    if (card) {
      api.get(`/restaurants/${restaurantId}/customer/orders`).then((r) => setOrders(r.data)).catch(() => setOrders([]));
    }
  }, [card, restaurantId]);

  const accent = restaurant?.primary_color || "#D9531E";
  const menuLink = `/menu/${restaurantId}${table ? `?table=${table}` : ""}`;

  if (card === undefined)
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAF7F2]">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: accent }} />
      </div>
    );

  return (
    <div className="max-w-md mx-auto min-h-screen bg-[#FAF7F2] px-5 py-8">
      <Link to={menuLink} data-testid="account-back-to-menu" className="inline-flex items-center gap-1 text-sm text-[#6B6560] mb-5 hover:text-[#1F1D1B]">
        <ArrowLeft className="h-4 w-4" /> {t("backToMenu")}
      </Link>
      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 rounded-2xl overflow-hidden bg-white grid place-items-center shadow">
          {restaurant?.logo_path ? <img src={fileUrl(restaurant.logo_path)} alt="logo" className="h-full w-full object-cover" /> : <ChefHat className="h-6 w-6" style={{ color: accent }} />}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-xl font-bold text-[#1F1D1B]">{restaurant?.name || t("restaurant")}</h1>
          <p className="text-xs text-[#6B6560]">{t("myAccount")}</p>
        </div>
        <LanguagePicker />
      </div>

      {card ? (
        <div className="space-y-4 fade-up" data-testid="customer-account">
          <div className="flex items-center justify-between">
            <p className="text-sm text-[#6B6560]">{t("hi")} <b data-testid="account-name">{card.member.name || card.member.phone}</b></p>
            <button data-testid="customer-logout-button" onClick={async () => { await logout(); toast.success(t("loggedOut")); }} className="inline-flex items-center gap-1 text-sm text-[#6B6560] hover:text-[#1F1D1B]">
              <LogOut className="h-4 w-4" /> {t("logOut")}
            </button>
          </div>
          {card.program?.active && <StampCard program={card.program} member={card.member} accent={accent} />}
          {card.program?.active && <RewardsHistory history={card.member.reward_history} accent={accent} />}
          <OrderHistory orders={orders} currency={restaurant?.currency_symbol} />
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E6E1DA] p-6">
          <p className="text-sm text-[#6B6560] mb-4">{t("loginToSee")}</p>
          <CustomerAuthForm restaurantId={restaurantId} accent={accent} onSuccess={(d) => { setCard(d); toast.success(t("welcome")); }} />
        </div>
      )}
    </div>
  );
}
